import React, { useEffect, useState } from "react";
import {
  useFormikContext,
  FormikContextType,
  FieldAttributes,
  useField,
  ErrorMessage,
} from "formik";
import toPath from "lodash/toPath";
import classNames from "classnames";
import { FormField } from "@avamae/formbuilder";
import { parseListDate } from "helpers/parseListDate";
import {
  NestedFormModuleData,
  setNestedFormModuleData,
} from "reducers/nestedCrudMenu";
import { APP_ROUTES, endpoints } from "config";
import { useDispatch } from "react-redux";
import Entity from "components/Entity";
import {
  workflowsReadOnlyRightsRenderer,
  workflowsRightsRenderer,
} from "pages/Workflows/Workflow";
import { Modal } from "components/modal/Modal";

/**
 * Given an object and a path, traverse the object to find the value,
 * and return it.
 */
export function deepGet(obj: any, path: string | string[]) {
  const segments = toPath(path);
  let i = 0;
  let val = obj;
  for (; i < segments.length; i++) {
    if (val === undefined) {
      return val;
    }
    val = val[segments[i]];
  }

  return val;
}

function Input<T>(props: FieldAttributes<T>) {
  const [field, meta] = useField(props);
  const showError = meta.touched && meta.error;
  const { className: existingClasses, ...rest } = props;
  const className = classNames(existingClasses, { ErrorInput: showError });

  return <input {...(field as any)} {...rest} className={className} />;
}

const ErrorM: React.FC<{ name: string }> = props => (
  <ErrorMessage name={props.name}>
    {msg => <div className="ErrorLabel">{msg}</div>}
  </ErrorMessage>
);

export interface WorkflowProgressProps {
  fieldName: string;
  label: string;
  readOnly?: boolean;
  showFormLabel?: boolean;
  showViewWorkflow?: boolean;
  triggerCommentFieldName?: string;
}

const WorkflowProgress = function<T>({
  fieldName,
  label,
  readOnly,
  showFormLabel,
  showViewWorkflow,
  triggerCommentFieldName,
}: WorkflowProgressProps) {
  const formikContext = useFormikContext<FormikContextType<T>>();
  const [field] = useField<any>(fieldName);
  const [triggerIndexes, setTriggerIndexes] = useState<number[] | null>(null);
  const [showViewWorkflowPopUp, setShowViewWorkflowPopUp] = useState<boolean>(
    false
  );
  const [triggerComment, setTriggerComment] = useState<string | null>(null);

  useEffect(() => {
    if (
      triggerCommentFieldName &&
      formikContext &&
      formikContext.values &&
      formikContext.values.hasOwnProperty(triggerCommentFieldName)
    ) {
      const tempTriggerMessage =
        formikContext.values[
          triggerCommentFieldName as keyof typeof formikContext.values
        ];

      if (typeof tempTriggerMessage === "string") {
        setTriggerComment(tempTriggerMessage);
      }
    }
  }, [triggerCommentFieldName, formikContext]);

  const values: any = formikContext.values;

  useEffect(() => {
    if (Array.isArray(field.value)) {
      const tempTriggerIndexes = field.value.map((item, idx) => {
        if (item.type === "Trigger") {
          return idx;
        } else {
          return null;
        }
      });

      if (tempTriggerIndexes.length > 0) {
        setTriggerIndexes(
          tempTriggerIndexes.filter(item => item !== null) as number[]
        );
      } else {
        setTriggerIndexes(null);
      }
    } else {
      setTriggerIndexes(null);
    }
  }, [field.value]);

  const viewWorkflow = () => {
    if (values && values.workflowId) {
      setShowViewWorkflowPopUp(true);
    }
  };

  if (readOnly) {
    return (
      <div className="FormBox FormSideBySide">
        {showFormLabel && (
          <div className="FormLabel">
            <label>{label}</label>
          </div>
        )}
        <div className="FormField">
          <div className="WorkflowProgressFieldRenderer ReadOnly">
            {showViewWorkflow && (
              <div
                className={`ViewWorkflow ${
                  !(values && values.workflowId) ? "Disabled" : ""
                }`}
                onClick={viewWorkflow}
              >
                view workflow
              </div>
            )}

            {field.value &&
            Array.isArray(field.value) &&
            field.value.length > 0 ? (
              <div className="Tasks">
                {field.value.map((item, idx) => {
                  const completed = item.bCompleted ?? false;
                  const nextItemCompleted =
                    idx + 1 <= field.value.length - 1 &&
                    field.value[idx + 1].hasOwnProperty("bCompleted")
                      ? field.value[idx + 1].bCompleted
                      : false;

                  let triggerMessage: string | null = null;
                  let triggerReasonCode: string | null = null;

                  const bIsTrigger =
                    triggerIndexes && triggerIndexes.includes(idx);

                  if (bIsTrigger) {
                    if (item.comments) {
                      triggerMessage = item.comments;
                    }

                    if (item.reasonCode) {
                      triggerReasonCode = item.reasonCode;
                    }
                  }

                  const newWorkflow =
                    triggerIndexes && triggerIndexes.includes(idx);
                  const startOfCurrentWorkflow =
                    triggerIndexes &&
                    triggerIndexes[triggerIndexes.length - 1] === idx;

                  const subsequentTriggersIndexes: number[] =
                    triggerIndexes && triggerIndexes.length > 1
                      ? triggerIndexes.slice(1)
                      : [];

                  return (
                    <div
                      className={`Task ${completed ? "Completed" : ""} ${
                        nextItemCompleted ? "NextItemCompleted" : ""
                      }`}
                      key={`task-${idx}`}
                    >
                      <div
                        className="TaskCircle"
                        key={`task-circle-${idx}`}
                      ></div>

                      {!startOfCurrentWorkflow &&
                      !subsequentTriggersIndexes.includes(idx) ? (
                        <>
                          {newWorkflow && (
                            <div className="WorkflowName">
                              Initial workflow triggered:{" "}
                              {item.workflowName ?? "-"}
                            </div>
                          )}
                          <div className="TaskName" key={`task-name-${idx}`}>
                            {`${item.type ? `${item.type}: ` : ""}${item.name ??
                              "-"}` ?? "-"}
                          </div>
                          <ul>
                            {item.description && (
                              <li
                                className="Description"
                                key={`task-description-${idx}`}
                              >
                                {item.description ?? "-"}
                              </li>
                            )}

                            {item.completionDate && (
                              <li
                                className="CompletionDate"
                                key={`task-completion-date-${idx}`}
                              >
                                {`Completed: ${parseListDate(
                                  item.completionDate,
                                  false
                                ) ?? "-"}`}
                              </li>
                            )}

                            {item.completedBy && (
                              <li
                                className="CompletedBy"
                                key={`task-completed-by-${idx}`}
                              >
                                {`Completed by: ${item.completedBy ?? "-"}`}
                              </li>
                            )}

                            {item.comments && (
                              <li
                                className="Comments"
                                key={`task-comments-${idx}`}
                              >
                                {`${item.comments ?? "-"}`}
                              </li>
                            )}

                            {item.reasonCode && (
                              <li
                                className="ReasonCode"
                                key={`task-reason -code-${idx}`}
                              >
                                {`Reason code: ${item.reasonCode ?? "-"}`}
                              </li>
                            )}
                          </ul>
                        </>
                      ) : !startOfCurrentWorkflow &&
                        subsequentTriggersIndexes.includes(idx) ? (
                        <div className="StartOfPreviousWorkflowContainer">
                          <div className="WorkflowName">
                            Previous workflow triggered:{" "}
                            {`${item.workflowName ?? "-"}`}
                          </div>

                          {(triggerMessage || triggerReasonCode) && (
                            <ul>
                              {triggerMessage && (
                                <li
                                  className="Comments"
                                  key={`task-comments-${idx}`}
                                >
                                  {`Message: ${triggerMessage ?? "-"}`}
                                </li>
                              )}

                              {triggerReasonCode && (
                                <li
                                  className="ReasonCode"
                                  key={`task-reason -code-${idx}`}
                                >
                                  {`Reason code: ${triggerReasonCode ?? "-"}`}
                                </li>
                              )}
                            </ul>
                          )}
                        </div>
                      ) : (
                        <div className="StartOfCurrentWorkflowContainer">
                          <div className="WorkflowName">
                            Current workflow triggered:{" "}
                            {`${item.workflowName ?? "-"}`}
                          </div>

                          {triggerComment && (
                            <div className="TriggerComment">
                              <div className="Name">Trigger comment:</div>

                              <div className="Comment">{triggerComment}</div>
                            </div>
                          )}

                          {(triggerMessage || triggerReasonCode) && (
                            <ul>
                              {triggerMessage && (
                                <li
                                  className="Comments"
                                  key={`task-comments-${idx}`}
                                >
                                  {`Message: ${triggerMessage ?? "-"}`}
                                </li>
                              )}

                              {triggerReasonCode && (
                                <li
                                  className="ReasonCode"
                                  key={`task-reason -code-${idx}`}
                                >
                                  {`Reason code: ${triggerReasonCode ?? "-"}`}
                                </li>
                              )}
                            </ul>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="NoData">No tasks </div>
            )}
            {showViewWorkflowPopUp && values && values.workflowId && (
              <Modal
                open={showViewWorkflowPopUp}
                closeModal={() => setShowViewWorkflowPopUp(false)}
                title={`View workflow`}
              >
                <ViewWorkflowPopUp
                  id={values.workflowId}
                  onDone={() => setShowViewWorkflowPopUp(false)}
                />
              </Modal>
            )}
          </div>
        </div>
      </div>
    );
  } else {
    return (
      <div className="FormBox FormSideBySide Boolean">
        <div className="FormLabel">
          <label>{label}</label>
        </div>

        <div className="FormField">
          <div className="RadioButtons">
            <div
              className={`Radio ${
                deepGet(formikContext.values, fieldName) === (true || "Yes")
                  ? "Checked"
                  : ""
              }`}
              role="checkbox"
              aria-checked={deepGet(formikContext.values, fieldName)}
              onClick={() =>
                formikContext.setFieldValue(fieldName as any, true)
              }
            >
              <input
                id={fieldName + "-yes"}
                type="radio"
                name={fieldName}
                value={"true"}
                checked={deepGet(formikContext.values, fieldName)}
                onChange={() => {}} // Prevent console error about providing a checked attribute but no onChange prop. The onChange is the onClick on the div that wraps this.
              />
              <label className="CustomInput" htmlFor={fieldName + "-yes"}>
                Yes
              </label>
            </div>

            <div
              className={`Radio ${
                !deepGet(formikContext.values, fieldName) ? "Checked" : ""
              }`}
              role="checkbox"
              aria-checked={!deepGet(formikContext.values, fieldName)}
              onClick={() =>
                formikContext.setFieldValue(fieldName as any, false)
              }
            >
              <input
                id={fieldName + "-no"}
                type="radio"
                name={fieldName}
                value={"false"}
                checked={!deepGet(formikContext.values, fieldName)}
                onChange={() => {}} // Prevent console error about providing a checked attribute but no onChange prop. The onChange is the onClick on the div that wraps this.
              />
              <label className="CustomInput" htmlFor={fieldName + "-no"}>
                No
              </label>
            </div>
          </div>
        </div>
        <ErrorM name={fieldName.toString()} />
      </div>
    );
  }
};

export { WorkflowProgress };

export const workflowProgressFieldRenderer = (
  readOnly: boolean,
  showFormLabel?: boolean,
  showViewWorkflow?: boolean,
  triggerCommentFieldName?: string
) =>
  function workflowProgressFieldRenderer<T>(
    _key: React.ReactText,
    field: FormField<T>
  ) {
    return (
      <WorkflowProgress
        label={field.label as string}
        readOnly={readOnly}
        fieldName={field.name as string}
        showFormLabel={showFormLabel}
        showViewWorkflow={showViewWorkflow}
        triggerCommentFieldName={triggerCommentFieldName}
      />
    );
  };

type ViewWorkflowPopUpProps = {
  id: number;
  onDone: () => void;
};

const ViewWorkflowPopUp: React.FC<ViewWorkflowPopUpProps> = ({
  id,
  onDone,
}) => {
  return (
    <>
      <Entity
        initialDataUrl={endpoints.workflows.view}
        readOnly={true}
        submitUrl={endpoints.workflows.view}
        onDone={onDone}
        selectedData={[{ workflowInstances__ID: id }]}
        idFromRowOption={id}
        readOnlyFields={undefined}
        formType="json"
        fieldRenderers={workflowsRightsRenderer}
        validationSchema={undefined}
        readOnlyFieldRenderers={workflowsReadOnlyRightsRenderer}
        hiddenFields={["bExistingUser", "workflowName"]}
        hiddenGroups={["Workflow Name"]}
        nullStringValues={[]}
        hideGroupLabels={["Details"]}
        groupColumns={{ workflowprogress: 2 }}
        fetchConfig={{
          params: {
            Id: id,
          },
        }}
        fieldsInBoxes={true}
        conditionalFields={
          {
            ProjectsID: {
              dependentField: "workflowsID",
              dependentValues: null,
              dependentType: "number",
            },
          }

          // When "Other user" is ticked, the companies multi-select should appear (and is mandatory), and it doesn't appear when "Principle user" is ticked
        }
        hideGroupLabel
      />
    </>
  );
};
